import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getInvestorAnnouncements, type InvestorAnnouncementRecord } from '../../utils/supabase';

const Page = styled.div`
  padding: 24px 20px 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 16px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnnouncementItem = styled.div`
  border-bottom: 1px solid #222222;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const AnnouncementMeta = styled.div`
  font-size: 11px;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const AnnouncementText = styled.div`
  font-size: 14px;
  color: #ffffff;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: #71717a;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
`;

const RefreshButton = styled.button`
  align-self: flex-start;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #333333;
  background: #111111;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
    background: #1a1a1a;
  }
`;

const InvestorAnnouncementsPWA: React.FC = () => {
  const investorName = localStorage.getItem('investorName') || '';
  const [announcements, setAnnouncements] = useState<InvestorAnnouncementRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnnouncements = async () => {
    setLoading(true);
    setError('');
    const result = await getInvestorAnnouncements(investorName);
    if (!result.success) {
      setError(result.error || 'Failed to load announcements.');
      setAnnouncements([]);
      setLoading(false);
      return;
    }
    setAnnouncements(result.announcements || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 30000);
    return () => clearInterval(interval);
  }, [investorName]);

  return (
    <Page>
      <Title>Announcements</Title>
      <Card>
        <RefreshButton onClick={fetchAnnouncements}>Refresh</RefreshButton>
        {error && <ErrorText>{error}</ErrorText>}
        {loading && !error && <EmptyState>Loading announcements...</EmptyState>}
        {!loading && announcements.length === 0 && <EmptyState>No announcements yet.</EmptyState>}
        {!loading && announcements.length > 0 && (
          <>
            {announcements.map((announcement) => (
              <AnnouncementItem key={announcement.id || announcement.created_at}>
                <AnnouncementMeta>
                  {announcement.investor_name ? `Direct • ${announcement.investor_name}` : 'All Investors'}
                  {announcement.created_at ? ` • ${new Date(announcement.created_at).toLocaleString()}` : ''}
                </AnnouncementMeta>
                <AnnouncementText>{announcement.message}</AnnouncementText>
              </AnnouncementItem>
            ))}
          </>
        )}
      </Card>
    </Page>
  );
};

export default InvestorAnnouncementsPWA;
